import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
  stagger,
  query,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import { WorkersService } from './worker.service';
import { Worker } from './worker.models';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';

interface StarInfo {
  type: 'full' | 'half' | 'empty';
}

interface DisplayWorker extends Worker {
  starArray: StarInfo[];
  starDelays: string[];
}

@Component({
    selector: 'app-workers',
    templateUrl: './worker.component.html',
    styleUrls: ['./worker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('titleEnter', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-30px)' }),
                animate('700ms 200ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
        trigger('subtitleEnter', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-20px)' }),
                animate('600ms 500ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
        trigger('cardsStagger', [
            transition(':enter', [
                query('.worker-card', [
                    style({ opacity: 0, transform: 'translateY(40px) scale(0.95)' }),
                    stagger('60ms', [
                        animate('500ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
                    ]),
                ], { optional: true }),
            ]),
        ]),
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
        ]),
    ],
    imports: [NgClass]
})
export class WorkersComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() sectionTitle = 'בעלי מקצוע מומלצים';
  @Input() sectionSubtitle = 'מצאו את איש המקצוע המתאים למשימה שלכם';
  @Input() limit = 36;
  @Input() offset = 1;

  @Output() contactWorker = new EventEmitter<Worker>();
  @Output() viewServices = new EventEmitter<Worker>();
  @Output() showMore = new EventEmitter<void>();

  @ViewChild('scrollWrapper') scrollWrapperRef!: ElementRef<HTMLDivElement>;

  workers: Worker[] = [];
  displayWorkers: DisplayWorker[] = [];
  loading = false;
  error: string | null = null;
  totalCount = 0;
  showCards = false;
  showMoreCard = false;
  hoveredCardId: number | null = null;

  private readonly MAX_VISIBLE = 18;

  // Scroll state
  canScrollLeft = false;
  canScrollRight = false;

  // Drag-to-scroll state
  private isDragging = false;
  private startX = 0;
  private scrollLeftPos = 0;
  private dragMoved = false;
  private boundDragMove: (e: MouseEvent) => void;
  private boundDragEnd: () => void;

  // Scroll throttle
  private scrollRafId: number | null = null;

  skeletonCards = Array(10).fill(0);

  private subs = new Subscription();

  constructor(
      private workersService: WorkersService,
      private ngZone: NgZone,
      private cdr: ChangeDetectorRef,
      private router: Router
  ) {
    this.boundDragMove = this.onDragMove.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);
  }

  ngOnInit(): void {
    this.subs.add(
        this.workersService.loading$.subscribe((l) => {
          this.loading = l;
          this.cdr.markForCheck();
        })
    );
    this.subs.add(
        this.workersService.error$.subscribe((e) => {
          this.error = e;
          this.cdr.markForCheck();
        })
    );
    this.subs.add(
        this.workersService.totalCount$.subscribe((c) => {
          this.totalCount = c;
          this.cdr.markForCheck();
        })
    );
    this.loadWorkers();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateScrollState(), 300);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup', this.boundDragEnd);
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId);
    }
  }

  loadWorkers(): void {
    this.showCards = false;
    this.cdr.markForCheck();

    this.subs.add(
        this.workersService.fetchWorkers(this.limit, this.offset).subscribe({
          next: (workers) => {
            this.workers = workers;
            this.showMoreCard = workers.length > this.MAX_VISIBLE;

            // Precompute star arrays and delays once
            this.displayWorkers = workers
                .slice(0, this.MAX_VISIBLE)
                .map((w, i) => {
                  const starArray = this.buildStarArray(w.rating);
                  const starDelays = starArray.map(
                      (_, j) => `${600 + i * 60 + j * 80}ms`
                  );
                  return { ...w, starArray, starDelays } as DisplayWorker;
                });

            setTimeout(() => {
              this.showCards = true;
              this.cdr.markForCheck();
              setTimeout(() => this.updateScrollState(), 200);
            }, 150);

            this.cdr.markForCheck();
          },
          error: () => {},
        })
    );
  }

  retry(): void {
    this.loadWorkers();
  }

  // ── Star computation (called once per worker, not in template) ──

  private buildStarArray(rating: number): StarInfo[] {
    const stars: StarInfo[] = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.3 && rating % 1 <= 0.7;
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) stars.push({ type: 'full' });
      else if (i === fullStars && hasHalf) stars.push({ type: 'half' });
      else stars.push({ type: 'empty' });
    }
    return stars;
  }

  // ── Scroll Navigation ──

  scrollBy(delta: number): void {
    const el = this.scrollWrapperRef?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  onScroll(): void {
    // Throttle scroll handler with requestAnimationFrame
    if (this.scrollRafId) return;
    this.scrollRafId = requestAnimationFrame(() => {
      this.updateScrollState();
      this.scrollRafId = null;
    });
  }

  private updateScrollState(): void {
    const el = this.scrollWrapperRef?.nativeElement;
    if (!el) return;
    const sl = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;

    const newCanScrollRight = sl < 0;
    const newCanScrollLeft = Math.abs(sl) < maxScroll - 1;

    if (
        newCanScrollRight !== this.canScrollRight ||
        newCanScrollLeft !== this.canScrollLeft
    ) {
      this.canScrollRight = newCanScrollRight;
      this.canScrollLeft = newCanScrollLeft;
      this.cdr.markForCheck();
    }
  }

  // ── Drag to Scroll ──

  onDragStart(event: MouseEvent): void {
    const el = this.scrollWrapperRef?.nativeElement;
    if (!el) return;
    this.isDragging = true;
    this.dragMoved = false;
    this.startX = event.pageX;
    this.scrollLeftPos = el.scrollLeft;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundDragMove);
      document.addEventListener('mouseup', this.boundDragEnd);
    });
  }

  private onDragMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    event.preventDefault();
    const el = this.scrollWrapperRef?.nativeElement;
    if (!el) return;
    const dx = event.pageX - this.startX;
    if (Math.abs(dx) > 5) {
      if (!this.dragMoved) {
        this.dragMoved = true;
        el.classList.add('is-dragging');
      }
    }
    if (this.dragMoved) {
      // Keep the grabbed content moving with the pointer, matching the
      // shared homepage rails.
      el.scrollLeft = this.scrollLeftPos - dx;
    }
  }

  private onDragEnd(): void {
    this.isDragging = false;
    const el = this.scrollWrapperRef?.nativeElement;
    if (el) el.classList.remove('is-dragging');
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup', this.boundDragEnd);
    this.ngZone.run(() => this.updateScrollState());
    setTimeout(() => (this.dragMoved = false), 50);
  }

  // ── Card Interactions (CSS-driven, minimal CD) ──

  onCardEnter(workerId: number): void {
    if (!this.dragMoved) {
      this.hoveredCardId = workerId;
      this.cdr.markForCheck();
    }
  }

  onCardLeave(workerId: number): void {
    if (this.hoveredCardId === workerId) {
      this.hoveredCardId = null;
      this.cdr.markForCheck();
    }
  }

  onContactClick(worker: Worker): void {
    this.contactWorker.emit(worker);
  }

  onServicesClick(worker: Worker): void {
    this.viewServices.emit(worker);
  }

  onShowMoreClick(): void {
    if (this.dragMoved) return;
    this.showMore.emit();
    this.router.navigate(['shop/coworker']).then(() => {});
  }

  trackByWorkerId(_: number, worker: DisplayWorker): number {
    return worker.id;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src =
        'https://ui-avatars.com/api/?background=1b4f8a&color=fff&size=300&font-size=0.4&name=?';
  }

    navigateToPage(pageId: number) {
        this.router.navigate([`./single/${pageId}`])
    }
}
